import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Sparkles, Send, Loader2, CheckCircle, Bot, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AIFormCreator = ({ onFormCreated }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your AI form builder assistant. I'll help you create a professional form for Williams Diversified LLC. What type of form would you like to create?"
    }
  ]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [formCreated, setFormCreated] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!userInput.trim() || loading) return;

    const newUserMessage = { role: 'user', content: userInput };
    setMessages(prev => [...prev, newUserMessage]);
    setUserInput('');
    setLoading(true);

    try {
      // Prepare conversation history
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await api.post('/forms/ai-create', {
        conversation_history: conversationHistory,
        user_message: userInput
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response
      };
      setMessages(prev => [...prev, assistantMessage]);

      // If form is complete
      if (response.data.is_complete && response.data.form_template) {
        setFormCreated(true);
        toast.success('Form template created! Saving...');
        
        // Save the form template
        try {
          await api.post('/forms/templates', response.data.form_template);
          toast.success('Form template saved successfully!');
          if (onFormCreated) {
            onFormCreated(response.data.form_template);
          }
        } catch (saveError) {
          toast.error('Form created but failed to save. Please try again.');
        }
      }
    } catch (error) {
      console.error('AI Form Creator Error:', error);
      toast.error('Failed to communicate with AI. Please try again.');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again or rephrase your request."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-black via-[#C9A961]/20 to-black border-b-2 border-[#C9A961] p-6 mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#C9A961] p-3 rounded-full">
            <Sparkles className="h-6 w-6 text-black" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#C9A961]">AI Form Creator</h2>
            <p className="text-gray-400">Powered by GPT-5 • Conversational Form Building</p>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role === 'assistant' && (
              <div className="bg-[#C9A961] p-2 rounded-full h-10 w-10 flex items-center justify-center flex-shrink-0">
                <Bot className="h-5 w-5 text-black" />
              </div>
            )}
            
            <Card className={`max-w-[80%] p-4 ${
              message.role === 'user'
                ? 'bg-[#C9A961] text-black border-[#C9A961]'
                : 'bg-black/60 text-white border-[#C9A961]/30'
            }`}>
              <p className="whitespace-pre-wrap">{message.content}</p>
            </Card>

            {message.role === 'user' && (
              <div className="bg-gradient-to-br from-[#C9A961] to-[#D4C278] p-2 rounded-full h-10 w-10 flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-black" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3 justify-start">
            <div className="bg-[#C9A961] p-2 rounded-full h-10 w-10 flex items-center justify-center">
              <Bot className="h-5 w-5 text-black" />
            </div>
            <Card className="bg-black/60 text-white border-[#C9A961]/30 p-4">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-[#C9A961]" />
                <span className="text-gray-400">AI is thinking...</span>
              </div>
            </Card>
          </div>
        )}

        {formCreated && (
          <div className="bg-gradient-to-r from-green-500/20 to-[#C9A961]/20 border-2 border-[#C9A961] rounded-xl p-6 text-center">
            <CheckCircle className="h-16 w-16 text-[#C9A961] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-[#C9A961] mb-2">Form Template Created!</h3>
            <p className="text-gray-300">Your form has been successfully created and saved.</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {!formCreated && (
        <div className="border-t-2 border-[#C9A961] bg-black/40 p-6">
          <div className="flex gap-3">
            <Input
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message... (Press Enter to send)"
              disabled={loading}
              className="flex-1 bg-black/60 border-[#C9A961]/30 text-white placeholder:text-gray-500 h-12 text-lg"
            />
            <Button
              onClick={handleSendMessage}
              disabled={loading || !userInput.trim()}
              className="bg-[#C9A961] text-black hover:bg-[#D4C278] h-12 px-8"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          <p className="text-gray-500 text-sm mt-2 text-center">
            💡 Tip: Be specific about the form type and required fields for best results
          </p>
        </div>
      )}
    </div>
  );
};

export default AIFormCreator;
